import React, { useEffect, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@av/ui/popover';
import { Button, ButtonProps } from '@av/ui/button';
import { cn } from '@av/utils';
import { Label } from '@av/ui/label';
import { Checkbox } from '@av/ui/form';
import { ChevronDown } from 'lucide-react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';

const ProductSpecificationFilter = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    specification: {
      id: number;
      title: string;
      values: {
        id: number;
        value: string;
      }[];
    };
  }
>(({ className, specification, ...props }, ref) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    const specValues = searchParams.get('specificaties');
    if (specValues) {
      const parsedValues = specValues.split(',');
      setSelected(
        parsedValues
          .filter((v) => v.startsWith(`${specification.title}:`))
          .map((v) => v.split(':')[1])
          .filter((v): v is string => v !== undefined)
      );
    } else {
      setSelected([]);
    }
  }, [searchParams, specification.title]);

  const handleChange = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];

    const params = new URLSearchParams(searchParams);
    const currentSpecValues = params.get('specificaties');
    let allValues = currentSpecValues ? currentSpecValues.split(',') : [];

    // Remove all values for this specification
    allValues = allValues.filter((v) => !v.startsWith(`${specification.title}:`));

    // Add new values for this specification
    newSelected.forEach((v) => allValues.push(`${specification.title}:${v}`));

    if (allValues.length > 0) {
      params.set('specificaties', allValues.join(','));
    } else {
      params.delete('specificaties');
    }

    const page = params.get('pagina');
    if (page && +page > 1) {
      params.delete('pagina');
    }

    router.push(`${pathname}?${params.toString()}`, undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          {...props}
          className={cn('h-9 rounded-full bg-white', className)}
          ref={ref}
        >
          {specification.title}
          <ChevronDown className="ml-1 size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-auto bg-white px-1 py-2">
        {specification.values.map((option) => {
          const key = `specification-value-${option.value}`;

          return (
            <div className="flex items-center px-3 py-2" key={option.id}>
              <Label className="w-full pr-10 text-base font-medium" htmlFor={key}>
                {option.value}
              </Label>
              <Checkbox
                checked={selected.includes(option.value)}
                id={key}
                name={key}
                className="size-5 border-[1.5px] border-gray-600 data-[state=checked]:border-primary"
                onCheckedChange={() => handleChange(option.value)}
              />
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
});
ProductSpecificationFilter.displayName = 'ProductSpecificationFilter';

export { ProductSpecificationFilter };
